import { useEffect, useRef, useState } from 'react';
import { Camera, RefreshCw, X, AlertCircle } from 'lucide-react';

interface CameraCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (imageElement: HTMLImageElement) => void;
}

export function CameraCaptureModal({ isOpen, onClose, onCapture }: CameraCaptureModalProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      return;
    }

    let active = true;

    async function initCamera() {
      // Stop previous stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('Acesso à câmera não é suportado pelo seu navegador.');
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode,
            width: { ideal: 1280 },
            height: { ideal: 1280 },
          },
          audio: false,
        });

        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (err: unknown) {
        if (!active) return;
        const e = err as { name?: string; message?: string };
        if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
          setError('Permissão de câmera não concedida. Por favor, permita o acesso à câmera nas configurações do navegador ou use o envio de arquivo.');
        } else if (e.name === 'NotFoundError' || e.name === 'DevicesNotFoundError') {
          setError('Nenhuma câmera encontrada no seu dispositivo.');
        } else {
          setError(e.message || 'Não foi possível iniciar a câmera.');
        }
      } finally {
        if (active) {
          setIsStarting(false);
        }
      }
    }

    initCamera();

    return () => {
      active = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [isOpen, facingMode]);

  // Flip camera
  const toggleCamera = () => {
    setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
  };

  // Capture frame
  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;

    const offscreenCanvas = document.createElement('canvas');
    offscreenCanvas.width = video.videoWidth;
    offscreenCanvas.height = video.videoHeight;
    const ctx = offscreenCanvas.getContext('2d');
    if (!ctx) return;

    // If front camera, flip horizontally for mirror preview
    if (facingMode === 'user') {
      ctx.translate(video.videoWidth, 0);
      ctx.scale(-1, 1);
    }

    ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

    const img = new Image();
    img.onload = () => {
      onCapture(img);
      onClose();
    };
    img.src = offscreenCanvas.toDataURL('image/png');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative flex w-full max-w-lg flex-col overflow-hidden rounded-3xl bg-[#06194d] text-white shadow-2xl border border-white/10">
        {/* Top Header */}
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-[#18e908]" />
            <h3 className="font-display text-xl font-bold uppercase tracking-wider text-white">
              Tirar foto agora
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
            aria-label="Fechar câmera"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Video Viewport */}
        <div className="relative aspect-square w-full bg-black overflow-hidden flex items-center justify-center">
          {error ? (
            <div className="p-6 text-center">
              <AlertCircle className="mx-auto h-12 w-12 text-rose-500 mb-3" />
              <p className="text-sm font-medium text-white/90 mb-4">{error}</p>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex rounded-xl bg-white/10 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/20 transition-colors"
              >
                Voltar e escolher arquivo
              </button>
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className={`h-full w-full object-cover ${
                  facingMode === 'user' ? 'scale-x-[-1]' : ''
                }`}
              />

              {/* Viewfinder Circle Target Overlay */}
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="h-3/4 w-3/4 rounded-full border-2 border-dashed border-[#18e908]/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.4)]" />
              </div>

              {isStarting && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-xs">
                  <div className="flex items-center gap-3 text-white">
                    <RefreshCw className="h-6 w-6 animate-spin text-[#18e908]" />
                    <span className="text-sm font-medium">Iniciando câmera...</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Action Controls */}
        {!error && (
          <div className="flex items-center justify-around bg-[#040e2d] px-6 py-5">
            <button
              type="button"
              onClick={toggleCamera}
              className="flex flex-col items-center gap-1 text-xs font-semibold text-white/80 hover:text-white transition-colors"
              title="Alternar câmera"
            >
              <div className="grid h-11 w-11 place-items-center rounded-full bg-white/10 hover:bg-white/20">
                <RefreshCw className="h-5 w-5" />
              </div>
              <span>Alternar</span>
            </button>

            <button
              type="button"
              onClick={handleCapture}
              disabled={isStarting}
              className="group relative flex h-18 w-18 items-center justify-center rounded-full bg-[#18e908] text-[#06194d] shadow-lg shadow-[#18e908]/30 transition-transform active:scale-95 disabled:opacity-50"
              aria-label="Capturar fotografia"
            >
              <div className="h-14 w-14 rounded-full border-4 border-[#06194d] flex items-center justify-center bg-[#18e908]">
                <Camera className="h-7 w-7 text-[#06194d]" />
              </div>
            </button>

            <button
              type="button"
              onClick={onClose}
              className="flex flex-col items-center gap-1 text-xs font-semibold text-white/80 hover:text-white transition-colors"
            >
              <div className="grid h-11 w-11 place-items-center rounded-full bg-white/10 hover:bg-white/20">
                <X className="h-5 w-5" />
              </div>
              <span>Cancelar</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
